import Channel from '@/Enums/Channel.enum';
import GeoAddressRadiusUpload from '@/components/Geography/GeoAddressRadiusUpload';
import GeoFencing from '@/components/Geography/GeoFencing';
import useGeoFencing from '@/components/Geography/GeoFencing/useGeoFencing';
import GeoTargetingZipCodesUpload from '@/components/Geography/GeoTargetingZipCodesUpload';
import LocationChips from '@/components/Geography/LocationChips';
import GeoMap from '@/components/Map/GeoMap';
import { GEO_TARGETING_LOCATIONS_LOOKUP } from '@/constants/endpoints';
import { STATES_REGEX, ZIP_CODES_REGEX } from '@/constants/geography';
import {
  getGeoSearchPlaceholder,
  getTypeFromRadius,
  handlePasteStatesAndZipCodes,
  handlePasteZipCodes,
  isZip,
  mergeZipsById,
} from '@/helpers/forecaster/geography';
import { useAppSelector } from '@/store/hooks/index.ts';
import { selectForecastGeoImpressions } from '@/store/selectors/forecaster.selectors.ts';
import { getGeoOptions, sendAddressRadius } from '@/store/slices/forecasterSlice';
import { Chip } from '@mui/material';
import axios from 'axios';
import classNames from 'classnames';
import debounce from 'debounce';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import GeoExclusion from './GeoExclusion';
import GeoTargeting from './GeoTargeting';

const GeoSearch = ({ options, mapClassName, loading, currentChannel }) => {
  const { control, setValue, getValues } = useFormContext();
  const dispatch = useDispatch();

  const { currentForecast } = useAppSelector(state => state.forecaster);
  const geoImpressions = useAppSelector(selectForecastGeoImpressions);

  //#region state
  const [query, setQuery] = useState('');
  const [loadingLookup, setLoadingLookup] = useState(false);
  const [notFoundLocations, setNotFoundLocations] = useState([]);
  const [missingBoundaryIds, setMissingBoundaryIds] = useState(new Set());
  const [open, setOpen] = useState(false);
  const [suggestions, setSuggestions] = useState([]);

  const nonDoohLocationsRef = useRef(null);
  const doohLocationsRef = useRef(null);
  const prevChannelRef = useRef(currentChannel);

  //#endregion
  const selectedLocations = useWatch({ name: 'geographic', control }) ?? [];
  const excludedLocations = useWatch({ name: 'geoExclusion', control });
  const onSelectedLocationsChange = useCallback(
    newSelectedLocations => {
      setValue('geographic', newSelectedLocations?.length ? newSelectedLocations : []);
    },
    [setValue],
  );
  const isGeoFencingSearch = useWatch({ control, name: 'isGeoFencingSearch' });

  const geoFence = useWatch({ name: 'geoFence', control });

  //#region callback
  const handleInput = useCallback((event, value) => {
    if (event?.type !== 'change') return;
    setQuery(value);
  }, []);

  const onValuesPaste = useCallback(
    async event => {
      const value = (event.clipboardData || window.clipboardData).getData('text').trim();

      let zips, states;

      if (ZIP_CODES_REGEX.test(value)) {
        zips = handlePasteZipCodes(value, selectedLocations);
      }

      if (STATES_REGEX.test(value)) {
        const res = handlePasteStatesAndZipCodes(value, selectedLocations);
        states = res.states;
        zips = res.zipCodes;
      }

      if ((!zips && !states) || (!zips?.length && !states?.length)) {
        return;
      }

      try {
        event.preventDefault();
        const existingNotFound = new Set(notFoundLocations.map(({ apiName }) => apiName));

        setLoadingLookup(true);
        const payload = await axios.post(GEO_TARGETING_LOCATIONS_LOOKUP, {
          zips,
          states,
        });
        setLoadingLookup(false);

        const { found, notFound } = payload.data || {};
        onSelectedLocationsChange([...selectedLocations, ...found]);

        setNotFoundLocations([
          ...notFoundLocations,
          ...notFound.filter(({ apiName }) => !existingNotFound.has(apiName)),
        ]);
      } catch (e) {
        toast.error('There was an error in handling pasted zip codes.');
        console.error(e?.message);
        setLoadingLookup(false);
      }
    },
    [selectedLocations, notFoundLocations, setNotFoundLocations, onSelectedLocationsChange],
  );

  const handleMissingBoundaries = useCallback(missingIds => {
    setMissingBoundaryIds(prev => {
      const newIds = missingIds.filter(id => !prev.has(id));
      return newIds.length ? new Set([...prev, ...newIds]) : prev;
    });
  }, []);

  const missingBoundaryLocations = selectedLocations.filter(({ id }) => missingBoundaryIds.has(id));

  const onChange = useCallback(
    (_, data) => {
      onSelectedLocationsChange(data);
      const notFoundIdsSet = new Set(notFoundLocations.map(({ id }) => id));
      onSelectedLocationsChange(data.filter(rec => !notFoundIdsSet.has(rec.id)));
    },
    [notFoundLocations, onSelectedLocationsChange],
  );

  const onCSVUpload = useCallback(
    async zipCodes => {
      if (!zipCodes?.length) return;
      const payload = await axios.post(GEO_TARGETING_LOCATIONS_LOOKUP, {
        zips: zipCodes.filter(
          zip => ![...(selectedLocations || []), ...(notFoundLocations || [])].find(({ apiName }) => zip === apiName),
        ),
      });
      const { found } = payload.data || {};
      onSelectedLocationsChange([...(selectedLocations || []), ...(found || [])]);
    },
    [notFoundLocations, selectedLocations, onSelectedLocationsChange],
  );

  const onBulkRadiusUpload = async file => {
    const currentValues = getValues();
    const payload = {
      file,
      channel: currentValues.currentChannel,
      daysInRange: currentValues.daysInRange,
      frequencyCapPerDay: currentValues.frequencyCapPerDay,
      ...currentValues[currentValues.currentChannel],
    };
    try {
      await dispatch(sendAddressRadius(payload)).unwrap();
      toast.success('File successfully uploaded!\nWe will email you results back shortly.');
    } catch (err) {
      toast.error('Error sending file');
      throw err;
    }
  };

  const onRemove = useCallback(
    locationId => () => {
      onSelectedLocationsChange(selectedLocations.filter(({ id }) => locationId !== id));
    },
    [onSelectedLocationsChange, selectedLocations],
  );

  const onClearNotFound = useCallback(() => {
    setNotFoundLocations([]);
  }, [setNotFoundLocations]);
  //#endregion

  //#region radius - using shared hook
  const handleGeoFenceChange = useCallback(
    newGeoFence => {
      setValue('geoFence', newGeoFence);
    },
    [setValue],
  );

  const {
    address,
    setAddress,
    radius,
    suggestions: fencingSuggestions,
    handleAddressInput,
    handleAddressSelect,
    handleRadiusInput,
    handleClear: handleClearRadius,
  } = useGeoFencing({
    initialGeoFence: currentForecast.geoFence,
    onGeoFenceChange: handleGeoFenceChange,
  });

  const onClearSelected = useCallback(() => {
    onSelectedLocationsChange([]);
    handleClearRadius();
    setValue('geoExclusion', []);
  }, [onSelectedLocationsChange, setValue, handleClearRadius]);
  //#end

  //#region Targeting
  const getOptions = useCallback(
    search => {
      dispatch(getGeoOptions(search));
    },
    [dispatch],
  );

  const debouncedFetch = useMemo(() => {
    return debounce(getOptions, 350);
  }, [getOptions]);

  const onOpen = useCallback(() => {
    setOpen(true);
  }, []);

  const onClose = useCallback(() => {
    setOpen(false);
    setSuggestions([]);
    setQuery('');
  }, []);

  useEffect(() => {
    if (!options?.length || !query) {
      setSuggestions([]);
      return;
    }
    setSuggestions(options);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options]);

  useEffect(() => {
    if (!query) {
      setSuggestions([]);
      return;
    }

    if (query.length < 2) return;

    debouncedFetch(query);

    return () => {
      debouncedFetch.clear();
    };
  }, [debouncedFetch, query]);

  const handleKeyDown = useCallback(
    async event => {
      if (event.key === ' ' || event.key === ',') {
        event.preventDefault();
        const zip = query.trim();

        const symbol = event.key;

        if (ZIP_CODES_REGEX.test(zip)) {
          try {
            setLoadingLookup(true);
            const payload = await axios.post(GEO_TARGETING_LOCATIONS_LOOKUP, {
              zips: [zip],
            });
            setLoadingLookup(false);
            const { found, notFound } = payload.data || {};
            onSelectedLocationsChange([...selectedLocations, ...(found || [])]);

            if (notFound?.length) {
              const existingNotFound = new Set(notFoundLocations.map(({ apiName }) => apiName));
              setNotFoundLocations([
                ...notFoundLocations,
                ...notFound.filter(({ apiName }) => !existingNotFound.has(apiName)),
              ]);
              toast.info('This zip code was not found');
            }

            setQuery('');
          } catch (error) {
            toast.error('There was an error, in checking zip code');
            console.error(error?.message);
            setLoadingLookup(false);
          }
        } else {
          setQuery(prev => prev + symbol);
        }
      }
    },
    [selectedLocations, onSelectedLocationsChange, query, notFoundLocations],
  );

  useEffect(() => {
    const prevChannel = prevChannelRef.current;

    if (currentChannel === Channel.Dooh && prevChannel !== Channel.Dooh) {
      nonDoohLocationsRef.current = selectedLocations;

      const zipFromOtherChannels = selectedLocations.filter(isZip);
      const savedDoohZips = doohLocationsRef.current ?? [];

      const mergedZips = mergeZipsById(zipFromOtherChannels, savedDoohZips);
      onSelectedLocationsChange(mergedZips);
    }

    if (currentChannel !== Channel.Dooh && prevChannel === Channel.Dooh) {
      doohLocationsRef.current = selectedLocations;

      if (nonDoohLocationsRef.current) {
        onSelectedLocationsChange(nonDoohLocationsRef.current);
      }
    }

    prevChannelRef.current = currentChannel;
  }, [currentChannel, selectedLocations, onSelectedLocationsChange]);

  //#endregion

  return (
    <div className='flex flex-col gap-3'>
      {isGeoFencingSearch ? (
        <>
          <GeoFencing
            address={address}
            setAddress={setAddress}
            handleAddressInput={handleAddressInput}
            suggestions={fencingSuggestions}
            handleAddressSelect={handleAddressSelect}
            radius={radius}
            handleRadiusInput={handleRadiusInput}
          />
          <div className='flex justify-between print:hidden'>
            <GeoAddressRadiusUpload onUpload={onBulkRadiusUpload} />
          </div>
        </>
      ) : (
        <>
          <GeoTargeting
            id='geo-lookup-autocomplete'
            selectedLocations={selectedLocations}
            loadingLookup={loadingLookup}
            loading={loading}
            query={query}
            handleInput={handleInput}
            handleKeyDown={handleKeyDown}
            onChange={onChange}
            onValuesPaste={onValuesPaste}
            onOpen={onOpen}
            onClose={onClose}
            suggestions={suggestions}
            open={open}
            placeholder={getGeoSearchPlaceholder(currentChannel)}
          />
          <div className='flex justify-between print:hidden'>
            <GeoTargetingZipCodesUpload onUpload={onCSVUpload} />
            {!!selectedLocations?.length && (
              <Chip label='Clear selection' onClick={onClearSelected} variant='outlined' />
            )}
          </div>
        </>
      )}

      <LocationChips
        locations={selectedLocations}
        variant='success'
        onRemove={onRemove}
        geoFenceLocation={geoFence?.address}
        radiusChip={getTypeFromRadius(geoFence?.radius)}
        onGeoFenceLocationRemove={handleClearRadius}
      />

      {!!notFoundLocations.length && (
        <div className='flex gap-2 items-center print:hidden'>
          <p className='text-[#84818A] text-nowrap'>Not found:</p>
          <LocationChips locations={notFoundLocations} variant='error' onRemove={onRemove} />

          <Chip label='Clear not found' onClick={onClearNotFound} variant='outlined' />
        </div>
      )}
      {!!missingBoundaryLocations.length && (
        <p className='text-[#84818A] print:hidden'>
          Map preview unavailable for {missingBoundaryLocations.map(({ displayName }) => displayName).join(', ')} —
          still included in targeting.
        </p>
      )}
      <GeoExclusion options={options} loading={loading} currentChannel={currentChannel} />
      <div className={classNames('w-full h-[350px] geo-map', mapClassName)}>
        <GeoMap
          locationsToShow={selectedLocations}
          excludedLocations={excludedLocations}
          reportNotFoundLocations={handleMissingBoundaries}
          geoFence={geoFence}
          geoImpressions={geoImpressions}
        />
      </div>
    </div>
  );
};

GeoSearch.propTypes = {
  options: PropTypes.array,
  mapClassName: PropTypes.string,
  loading: PropTypes.bool,
  currentChannel: PropTypes.string,
};

export default GeoSearch;
